import { useState, useEffect, useCallback } from 'react';
import { FEATURE_GROUPS, hasFeature } from '../lib/plans';
import { SearchBox, Chips, matchesQuery } from './Filters';
import PasswordInput from './PasswordInput';
import {
  platformOrgDetails, platformUpdateOrg, platformOrgPeople, platformUpdatePerson, platformSetPersonPassword,
  platformSetOrgEntitlements, platformSetStatus, platformCreatePerson, platformSetPersonRole, platformExportOrg, downloadJson,
} from '../lib/org';

const STATUS = {
  trial:     { label: 'Prueba',     cls: 'bg-amber-100 text-amber-800' },
  active:    { label: 'Activa',     cls: 'bg-emerald-100 text-emerald-800' },
  suspended: { label: 'Suspendida', cls: 'bg-red-100 text-red-700' },
  cancelled: { label: 'Cancelada',  cls: 'bg-gray-200 text-gray-600' },
};
const TIMEZONES = ['America/Argentina/Buenos_Aires', 'America/Montevideo', 'America/Santiago', 'America/Sao_Paulo', 'America/Bogota', 'America/Lima', 'America/Mexico_City', 'America/New_York', 'Europe/Madrid', 'UTC'];
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const toDateInput = (d) => (d ? new Date(new Date(d).getTime() - new Date(d).getTimezoneOffset() * 60000).toISOString().slice(0, 10) : '');
const initials = (n) => String(n || '?').split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
const field = 'w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-sky-300';
const btnPrimary = 'px-5 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-40';
const LEVELS = { super_admin: 'Super administrador', admin: 'Administrador', employee: 'Empleado' };
const genPass = () => 'G360-' + Array.from(crypto.getRandomValues(new Uint8Array(6))).map((b) => b.toString(16).padStart(2, '0')).join('');
const grad = { background: 'var(--brand-grad)' };

function Card({ title, hint, children, right }) {
  return (
    <section className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5 sm:p-6">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div><h3 className="font-bold text-gray-900">{title}</h3>{hint && <p className="text-xs text-gray-400 mt-0.5">{hint}</p>}</div>
        {right}
      </div>
      {children}
    </section>
  );
}
const Label = ({ children }) => <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wide mb-1">{children}</label>;

function Stat({ label, value, sub }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-4">
      <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide">{label}</p>
      <p className="text-xl font-black text-gray-900 mt-1 truncate">{value}</p>
      {sub && <p className="text-[11px] text-gray-400 mt-0.5 truncate">{sub}</p>}
    </div>
  );
}

function PersonCard({ person, isOwner, onSaved, onError, onOk }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(person.name || '');
  const [email, setEmail] = useState(person.email || '');
  const [active, setActive] = useState(person.active !== false);
  const [pass, setPass] = useState('');
  const [level, setLevel] = useState(person.level);
  const [busy, setBusy] = useState(false);
  const emailChanged = email.trim().toLowerCase() !== (person.email || '').toLowerCase();
  const dirty = emailChanged || name.trim() !== (person.name || '') || active !== (person.active !== false);
  const save = async () => {
    if (emailChanged && !window.confirm(`El correo de ingreso de ${person.name} pasará a ser ${email.trim()}. El anterior dejará de funcionar. ¿Confirmás?`)) return;
    setBusy(true);
    try { await platformUpdatePerson(person.id, { name, email, active }); onOk('Persona actualizada'); await onSaved(); }
    catch (e) { onError(e.message || 'Error'); }
    setBusy(false);
  };
  const saveRole = async () => {
    if (!window.confirm(`${person.name} pasará a ser ${LEVELS[level].toLowerCase()}. ¿Confirmás?`)) return;
    setBusy(true);
    try { await platformSetPersonRole(person.id, level); onOk('Rol actualizado'); await onSaved(); }
    catch (e) { onError(e.message || 'Error'); setLevel(person.level); }
    setBusy(false);
  };
  const resetPass = async () => {
    setBusy(true);
    try { await platformSetPersonPassword(person.id, pass); setPass(''); onOk('Clave actualizada'); }
    catch (e) { onError(e.message || 'Error'); }
    setBusy(false);
  };
  return (
    <div className={`rounded-2xl border ${open ? 'border-sky-200 bg-sky-50/30' : 'border-gray-100 bg-white'}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 p-3.5 text-left">
        <span className={`w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0 ${person.active === false ? 'bg-gray-300' : ''}`} style={person.active === false ? undefined : grad}>{initials(person.name)}</span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2"><span className="font-semibold text-gray-900 text-sm truncate">{person.name}</span>
            {person.level === 'super_admin' && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">Super admin</span>}
            {isOwner && person.level !== 'super_admin' && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">Dueño</span>}
            {person.active === false && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">Inactivo</span>}
          </span>
          <span className="block text-xs text-gray-400 truncate">{person.email}</span>
        </span>
        <span className="text-[11px] text-gray-400 hidden sm:block">{person.last_sign_in_at ? `Último ingreso ${fmtDate(person.last_sign_in_at)}` : 'Nunca ingresó'}</span>
        <span className="text-gray-300 text-lg">{open ? '−' : '+'}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-sky-100 pt-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Nombre</Label><input value={name} onChange={(e) => setName(e.target.value)} className={field} /></div>
            <div><Label>Correo de ingreso</Label><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={field} /></div>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-600"><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Cuenta activa (puede ingresar)</label>
          <div className="flex justify-end"><button disabled={busy || !dirty} onClick={save} className={btnPrimary} style={grad}>Guardar cambios</button></div>
          {person.level !== 'kiosk' && (
            <div className="border-t border-gray-100 pt-3">
              <Label>Rol dentro de la organización</Label>
              <div className="flex gap-2">
                <select value={level} onChange={(e) => setLevel(e.target.value)} className={`${field} flex-1`}>
                  {Object.entries(LEVELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <button disabled={busy || level === person.level} onClick={saveRole} className="px-4 rounded-xl text-sm font-bold bg-gray-900 text-white disabled:opacity-40">Cambiar rol</button>
              </div>
              <p className="text-[11px] text-gray-400 mt-1">El super administrador gestiona todo, incluidos otros administradores. El administrador queda con los permisos que el super administrador le defina.</p>
            </div>
          )}
          <div className="border-t border-gray-100 pt-3">
            <Label>Restablecer clave</Label>
            <div className="flex gap-2">
              <div className="flex-1"><PasswordInput value={pass} onChange={(e) => setPass(e.target.value)} placeholder="Nueva clave (mínimo 8)" className={field} /></div>
              <button disabled={busy || pass.length < 8} onClick={resetPass} className="px-4 rounded-xl text-sm font-bold bg-gray-900 text-white disabled:opacity-40">Cambiar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AddPerson({ orgId, onDone, onError, onOk }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: '', email: '', password: genPass(), level: 'employee' });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await platformCreatePerson(orgId, f); onOk(`${LEVELS[f.level]} creado. Pasale el correo y la clave: ${f.password}`); setF({ name: '', email: '', password: genPass(), level: 'employee' }); setOpen(false); await onDone(); }
    catch (err) { onError(err.message || 'Error'); }
    setBusy(false);
  };
  if (!open) return <button onClick={() => setOpen(true)} className={btnPrimary} style={grad}>+ Agregar persona</button>;
  return (
    <form onSubmit={submit} className="bg-white rounded-3xl border border-sky-200 shadow-sm p-5 space-y-3">
      <div className="flex items-center justify-between"><h3 className="font-bold text-gray-900">Nueva persona</h3><button type="button" onClick={() => setOpen(false)} className="text-xs text-gray-400">Cancelar</button></div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label>Nombre y apellido</Label><input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={field} /></div>
        <div><Label>Correo de ingreso</Label><input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={field} /></div>
        <div><Label>Rol</Label>
          <select value={f.level} onChange={(e) => setF({ ...f, level: e.target.value })} className={field}>{Object.entries(LEVELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
        <div><Label>Clave inicial</Label>
          <div className="flex gap-2"><div className="flex-1"><PasswordInput required minLength={8} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={field} /></div>
            <button type="button" onClick={() => setF({ ...f, password: genPass() })} className="px-3 rounded-xl text-xs font-bold border border-gray-200 text-gray-600 bg-white">Generar</button></div></div>
      </div>
      <p className="text-[11px] text-gray-400">Entra por {window.location.host}/o/{'<enlace de la organización>'} con ese correo y clave. Respeta el límite de personas del plan.</p>
      <div className="flex justify-end"><button disabled={busy} className={btnPrimary} style={grad}>Crear persona</button></div>
    </form>
  );
}

function PeopleTab({ orgId, people, reload, onChange, onError, onOk }) {
  const [q, setQ] = useState('');
  const [scope, setScope] = useState('all');
  if (!people) return <p className="text-sm text-gray-400">Cargando personas…</p>;
  const owner = people.filter((p) => p.role === 'admin').sort((a, b) => new Date(a.created_at) - new Date(b.created_at))[0];
  const shown = people.filter((p) => matchesQuery(q, p.name, p.email) && (scope === 'all' || (scope === 'inactive' ? p.active === false : true)));
  const groups = [
    ['Super administradores', 'Controlan toda la organización, incluidos los demás administradores.', shown.filter((p) => p.level === 'super_admin')],
    ['Administradores', 'Gestionan el día a día según los permisos que se les den.', shown.filter((p) => p.level === 'admin')],
    ['Empleados', 'Marcan su asistencia y usan su propia vista.', shown.filter((p) => p.level === 'employee')],
    ['Kioscos', 'Cuentas de tablets fijas.', shown.filter((p) => p.level === 'kiosk')],
  ];
  return (
    <div className="space-y-5">
      <AddPerson orgId={orgId} onDone={async () => { await reload(); await onChange(); }} onError={onError} onOk={onOk} />
      <div className="flex gap-2 flex-wrap items-center">
        <SearchBox value={q} onChange={setQ} placeholder="Buscar por nombre o correo…" className="flex-1 min-w-[200px]" />
        <Chips value={scope} onChange={setScope} options={[{ id: 'all', label: 'Todos' }, { id: 'inactive', label: 'Inactivos' }]} />
      </div>
      {groups.map(([title, hint, list]) => (list.length || title === 'Super administradores' ? (
        <Card key={title} title={`${title} (${list.length})`} hint={hint}>
          <div className="space-y-2">
            {list.length === 0 && <p className="text-sm text-gray-400">No hay personas para mostrar.</p>}
            {list.map((p) => <PersonCard key={p.id + p.email} person={p} isOwner={owner?.id === p.id} onSaved={async () => { await reload(); await onChange(); }} onError={onError} onOk={onOk} />)}
          </div>
        </Card>
      ) : null))}
    </div>
  );
}

function DataTab({ org, onChange, onError, onOk }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let alive = true;
    platformOrgDetails(org.id).then((x) => { if (alive) setD({ ...x, trial_ends_at: toDateInput(x.trial_ends_at) }); }).catch((e) => onError(e.message || 'Error'));
    return () => { alive = false; };
  }, [org.id, org.slug]); // eslint-disable-line
  if (!d) return <p className="text-sm text-gray-400">Cargando…</p>;
  const set = (k, v) => setD((x) => ({ ...x, [k]: v }));
  const link = `${window.location.origin}/o/${d.slug}`;
  const slugChanged = d.slug !== org.slug;
  const addDays = (n) => { const base = d.trial_ends_at ? new Date(d.trial_ends_at + 'T12:00:00') : new Date(); const b = base < new Date() ? new Date() : base; b.setDate(b.getDate() + n); set('trial_ends_at', toDateInput(b)); };
  const save = async () => {
    if (slugChanged && !window.confirm(`El enlace cambiará de /o/${org.slug} a /o/${d.slug}. El anterior dejará de funcionar y todos deberán usar el nuevo. ¿Confirmás?`)) return;
    setBusy(true);
    try {
      await platformUpdateOrg(org.id, { ...d, trial_ends_at: d.trial_ends_at ? new Date(d.trial_ends_at + 'T23:59:00').toISOString() : null });
      onOk('Datos guardados'); await onChange();
    } catch (e) { onError(e.message || 'Error'); }
    setBusy(false);
  };
  return (
    <div className="space-y-5">
      <Card title="Identidad y enlace de ingreso" hint="Es la dirección única que usan los administradores y empleados de esta organización.">
        <div className="space-y-3">
          <div><Label>Nombre de la organización</Label><input value={d.name || ''} onChange={(e) => set('name', e.target.value)} className={field} /></div>
          <div>
            <Label>Enlace</Label>
            <div className="flex items-center gap-1 border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm bg-white">
              <span className="text-gray-400 whitespace-nowrap">{window.location.host}/o/</span>
              <input value={d.slug || ''} onChange={(e) => set('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))} className="flex-1 min-w-0 outline-none font-semibold" />
            </div>
            <div className="flex gap-3 mt-1.5 text-xs">
              <button type="button" onClick={() => navigator.clipboard?.writeText(link).then(() => onOk('Enlace copiado'))} className="font-bold text-sky-600">Copiar enlace</button>
              <a href={link} target="_blank" rel="noreferrer" className="font-bold text-sky-600">Abrir</a>
              {slugChanged && <span className="text-amber-600 font-semibold">Al guardar, el enlace anterior dejará de funcionar</span>}
            </div>
          </div>
          <div><Label>Zona horaria</Label>
            <select value={d.timezone || ''} onChange={(e) => set('timezone', e.target.value)} className={field}>
              {[...new Set([d.timezone, ...TIMEZONES].filter(Boolean))].map((z) => <option key={z} value={z}>{z}</option>)}
            </select>
          </div>
        </div>
      </Card>
      <Card title="Mails que envía la organización" hint="El remitente real es el de la plataforma; acá se define cómo se ve y adónde llegan las respuestas.">
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Nombre del remitente</Label><input value={d.sender_name || ''} onChange={(e) => set('sender_name', e.target.value)} className={field} placeholder={d.name} /></div>
          <div><Label>Correo de respuesta</Label><input type="email" value={d.reply_to || ''} onChange={(e) => set('reply_to', e.target.value)} className={field} placeholder="info@organizacion.com" /></div>
        </div>
      </Card>
      <Card title="Período de prueba" hint="Solo aplica mientras la organización está en prueba.">
        <div className="flex gap-2 items-center flex-wrap">
          <input type="date" value={d.trial_ends_at || ''} onChange={(e) => set('trial_ends_at', e.target.value)} className={`${field} !w-auto`} />
          <button type="button" onClick={() => addDays(7)} className="px-3.5 py-2.5 rounded-xl text-xs font-bold border border-gray-200 text-gray-600 bg-white">+7 días</button>
          <button type="button" onClick={() => addDays(14)} className="px-3.5 py-2.5 rounded-xl text-xs font-bold border border-gray-200 text-gray-600 bg-white">+14 días</button>
        </div>
      </Card>
      <Card title="Notas internas" hint="Solo las ve la administración de la plataforma.">
        <textarea value={d.notes || ''} onChange={(e) => set('notes', e.target.value)} rows={4} className={field} placeholder="Acuerdos, pendientes, contacto…" />
      </Card>
      <div className="flex justify-end sticky bottom-4"><button disabled={busy} onClick={save} className={`${btnPrimary} shadow-lg`} style={grad}>Guardar datos</button></div>
    </div>
  );
}

function PlanTab({ org, plans, ent, onChange, onError, onOk }) {
  const [plan, setPlan] = useState(org.plan_id);
  const [override, setOverride] = useState(ent?.max_employees_override ? String(ent.max_employees_override) : '');
  const [locOverride, setLocOverride] = useState(ent?.max_locations_override ? String(ent.max_locations_override) : '');
  const [fo, setFo] = useState(ent?.feature_overrides || {});
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await platformSetOrgEntitlements(org.id, plan, override ? parseInt(override, 10) : null, locOverride ? parseInt(locOverride, 10) : null, fo); onOk('Plan y funciones actualizados'); await onChange(); }
    catch (e) { onError(e.message || 'Error'); }
    setBusy(false);
  };
  const cur = plans.find((x) => x.id === plan);
  return (
    <div className="space-y-5">
      <Card title="Plan y límites" hint="Los límites propios reemplazan a los del plan solo para esta organización.">
        <div className="grid sm:grid-cols-3 gap-3">
          <div><Label>Plan</Label>
            <select value={plan} onChange={(e) => setPlan(e.target.value)} className={field}>{plans.map((p) => <option key={p.id} value={p.id}>{p.name}{p.archived ? ' (archivado)' : ''}</option>)}</select></div>
          <div><Label>Máx. personas</Label><input value={override} onChange={(e) => setOverride(e.target.value.replace(/\D/g, ''))} placeholder={cur?.max_employees ? `Plan: ${cur.max_employees}` : 'Plan: sin límite'} className={field} /></div>
          <div><Label>Máx. sedes</Label><input value={locOverride} onChange={(e) => setLocOverride(e.target.value.replace(/\D/g, ''))} placeholder={cur?.max_locations ? `Plan: ${cur.max_locations}` : 'Plan: sin límite'} className={field} /></div>
        </div>
      </Card>
      <Card title="Funciones" hint={Object.keys(fo).length ? `${Object.keys(fo).length} ajuste(s) propios sobre el plan` : 'Todo según el plan'}>
        <div className="space-y-5">
          {FEATURE_GROUPS.map((g) => (
            <div key={g.id}>
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide mb-2">{g.label}</p>
              <div className="divide-y divide-gray-50">
                {g.items.map((f) => {
                  const planOn = hasFeature(cur?.features, f.key);
                  const v = fo[f.key] === undefined ? 'plan' : fo[f.key] ? 'on' : 'off';
                  const setV = (x) => setFo((o) => { const n = { ...o }; if (x === 'plan') delete n[f.key]; else n[f.key] = x === 'on'; return n; });
                  return (
                    <div key={f.key} className="flex items-center justify-between gap-3 py-2">
                      <span className="text-sm text-gray-700">{f.label}</span>
                      <select value={v} onChange={(e) => setV(e.target.value)} className={`border rounded-lg px-2.5 py-1.5 bg-white text-xs ${v === 'plan' ? 'border-gray-200 text-gray-500' : 'border-sky-300 text-sky-700 font-bold'}`}>
                        <option value="plan">Según el plan ({planOn ? 'sí' : 'no'})</option>
                        <option value="on">Incluida</option>
                        <option value="off">No incluida</option>
                      </select>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </Card>
      <div className="flex justify-end sticky bottom-4"><button disabled={busy} onClick={save} className={`${btnPrimary} shadow-lg`} style={grad}>Guardar plan y funciones</button></div>
    </div>
  );
}

function ActionsTab({ org, onChange, onError, onOk }) {
  const [busy, setBusy] = useState(false);
  const run = async (fn, ok) => { setBusy(true); try { await fn(); onOk(ok); await onChange(); } catch (e) { onError(e.message || 'Error'); } setBusy(false); };
  const inactive = org.status !== 'active' && org.status !== 'trial';
  return (
    <div className="space-y-5">
      <Card title="Respaldo" hint="Descarga todos los datos de esta organización en un archivo JSON.">
        <button disabled={busy} onClick={() => run(async () => downloadJson(await platformExportOrg(org.id), `respaldo-${org.slug || 'org'}`), 'Respaldo descargado')} className="px-5 py-2.5 rounded-xl text-sm font-bold text-gray-700 border border-gray-200 bg-white disabled:opacity-50">Descargar respaldo</button>
      </Card>
      <Card title="Estado de la organización" hint={inactive ? 'Hoy nadie de esta organización puede ingresar.' : 'Suspender bloquea el ingreso de todas sus personas; los datos se conservan.'}>
        <div className="flex gap-2 flex-wrap">
          {inactive
            ? <button disabled={busy} onClick={() => run(() => platformSetStatus(org.id, 'active'), 'Organización reactivada')} className="px-5 py-2.5 rounded-xl text-sm font-bold bg-emerald-600 text-white disabled:opacity-50">Reactivar</button>
            : <button disabled={busy} onClick={() => window.confirm(`¿Suspender "${org.name}"? Nadie de esa organización podrá ingresar.`) && run(() => platformSetStatus(org.id, 'suspended'), 'Organización suspendida')} className="px-5 py-2.5 rounded-xl text-sm font-bold bg-red-50 text-red-600 border border-red-200 disabled:opacity-50">Suspender</button>}
          {org.status === 'trial' && <button disabled={busy} onClick={() => run(() => platformSetStatus(org.id, 'active'), 'Pasó a activa')} className="px-5 py-2.5 rounded-xl text-sm font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 disabled:opacity-50">Marcar como activa</button>}
        </div>
      </Card>
    </div>
  );
}

export default function OrgDetailPage({ org, usage, plans, ent, onBack, onChange, onError, onOk }) {
  const [tab, setTab] = useState('summary');
  const [people, setPeople] = useState(null);
  const reload = useCallback(async () => { try { setPeople(await platformOrgPeople(org.id)); } catch (e) { onError(e.message || 'Error'); } }, [org.id]); // eslint-disable-line
  useEffect(() => { reload(); }, [reload]);
  const st = STATUS[org.status] || STATUS.active;
  const link = `${window.location.origin}/o/${org.slug}`;
  const left = org.status === 'trial' && org.trial_ends_at ? Math.ceil((new Date(org.trial_ends_at).getTime() - Date.now()) / 86400000) : null;
  const admins = people ? people.filter((p) => p.role === 'admin').length : '…';
  const emps = people ? people.filter((p) => p.role === 'employee').length : '…';
  return (
    <div className="space-y-5">
      <div className="rounded-3xl p-5 sm:p-6 text-white" style={{ background: 'linear-gradient(135deg,#0f172a 0%,#222d5a 100%)' }}>
        <button onClick={onBack} className="text-xs font-bold text-white/60 hover:text-white mb-3">← Todas las organizaciones</button>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h2 className="text-2xl font-black tracking-tight truncate">{org.name}</h2>
            <p className="text-sm text-white/60 mt-0.5 truncate">{window.location.host}/o/{org.slug}</p>
          </div>
          <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${st.cls}`}>{st.label}</span>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Plan" value={<span className="capitalize">{org.plan_id}</span>} sub={left !== null ? (left <= 0 ? 'La prueba venció' : `Prueba: ${left} día${left === 1 ? '' : 's'}`) : undefined} />
        <Stat label="Personas" value={`${org.employees}${org.max_employees ? ` / ${org.max_employees}` : ''}`} sub={`${admins} admin · ${emps} empleados`} />
        <Stat label="Marcaciones del mes" value={usage?.checkins_month ?? '—'} sub={`Mes anterior: ${usage?.checkins_prev_month ?? '—'}`} />
        <Stat label="Último uso" value={usage?.last_activity ? fmtDate(usage.last_activity) : 'Nunca'} sub={`Alta ${fmtDate(org.created_at)}`} />
      </div>

      <Chips tabs value={tab} onChange={setTab} options={[{ id: 'summary', label: 'Resumen' }, { id: 'data', label: 'Datos y enlace' }, { id: 'people', label: 'Personas', count: people ? people.length : null }, { id: 'plan', label: 'Plan y funciones' }, { id: 'actions', label: 'Estado y respaldo' }]} />

      {tab === 'summary' && (
        <div className="space-y-5">
          <Card title="Ingreso de esta organización" hint="Administradores y empleados entran por el mismo enlace. Por /plataforma nadie de la organización puede ingresar.">
            <div className="flex items-center gap-2 flex-wrap">
              <code className="flex-1 min-w-0 truncate bg-gray-50 border border-gray-100 rounded-xl px-3.5 py-2.5 text-sm text-gray-700">{link}</code>
              <button onClick={() => navigator.clipboard?.writeText(link).then(() => onOk('Enlace copiado'))} className="px-4 py-2.5 rounded-xl text-xs font-bold text-white" style={grad}>Copiar</button>
              <a href={link} target="_blank" rel="noreferrer" className="px-4 py-2.5 rounded-xl text-xs font-bold text-gray-600 border border-gray-200">Abrir</a>
            </div>
          </Card>
          <Card title="Quién administra" hint="Cuentas con acceso de administración dentro de la organización.">
            {!people ? <p className="text-sm text-gray-400">Cargando…</p> : (
              <ul className="space-y-2">
                {people.filter((p) => p.role === 'admin').map((p) => (
                  <li key={p.id} className="flex items-center gap-3 text-sm">
                    <span className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold text-white" style={grad}>{initials(p.name)}</span>
                    <span className="font-semibold text-gray-800">{p.name}</span><span className="text-gray-400 truncate">{p.email}</span>
                  </li>
                ))}
                {admins === 0 && <li className="text-sm text-amber-600">Esta organización no tiene administradores.</li>}
              </ul>
            )}
            <button onClick={() => setTab('people')} className="mt-3 text-xs font-bold text-sky-600">Gestionar personas →</button>
          </Card>
          {org.status === 'trial' && left !== null && left <= 3 && (
            <div className="rounded-2xl bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-3">La prueba {left <= 0 ? 'ya venció' : `vence en ${left} día${left === 1 ? '' : 's'}`}. Podés extenderla desde “Datos y enlace”.</div>
          )}
        </div>
      )}
      {tab === 'data' && <DataTab org={org} onChange={onChange} onError={onError} onOk={onOk} />}
      {tab === 'people' && <PeopleTab orgId={org.id} people={people} reload={reload} onChange={onChange} onError={onError} onOk={onOk} />}
      {tab === 'plan' && <PlanTab org={org} plans={plans} ent={ent} onChange={onChange} onError={onError} onOk={onOk} />}
      {tab === 'actions' && <ActionsTab org={org} onChange={onChange} onError={onError} onOk={onOk} />}
    </div>
  );
}
